import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Wallet, Clock, CheckCircle, XCircle, AlertTriangle, TrendingUp, TrendingDown, Calculator } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { createWithdrawal, getWithdrawals, getPlatformSettings, getCryptoPrices } from '../services/api';
import { toast } from 'sonner';

const WithdrawPage = () => {
  const { user, refreshUser } = useAuth();
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [cryptoPrices, setCryptoPrices] = useState({});

  const cryptoOptions = [
    { id: 'BTC', name: 'Bitcoin', icon: '₿', color: 'from-orange-500 to-yellow-500' },
    { id: 'ETH', name: 'Ethereum', icon: 'Ξ', color: 'from-purple-500 to-blue-500' },
    { id: 'USDT', name: 'Tether', icon: '₮', color: 'from-emerald-500 to-green-500' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [withdrawalsRes, settingsRes] = await Promise.all([
        getWithdrawals(),
        getPlatformSettings()
      ]);
      setWithdrawals(withdrawalsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
    try {
      const pricesRes = await getCryptoPrices();
      setCryptoPrices(pricesRes.data);
    } catch { /* silent fail */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const withdrawAmount = parseFloat(amount);
    const minWithdrawal = parseFloat(settings.min_withdrawal || 20);
    
    if (withdrawAmount < minWithdrawal) {
      toast.error(`Minimum withdrawal is $${minWithdrawal}`);
      return;
    }

    if (withdrawAmount > user?.balance) {
      toast.error('Insufficient balance');
      return;
    }

    if (!walletAddress.trim()) {
      toast.error('Please enter your wallet address');
      return;
    }

    setLoading(true);
    try {
      await createWithdrawal({
        amount: withdrawAmount,
        crypto_type: selectedCrypto,
        wallet_address: walletAddress
      });
      toast.success('Withdrawal request submitted! Processing time: 24-48 hours.');
      setAmount('');
      setWalletAddress('');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit withdrawal');
    } finally {
      setLoading(false);
    }
  };

  const withdrawFeePercent = parseFloat(settings.withdrawal_fee || 1);
  const withdrawAmount = parseFloat(amount) || 0;
  const feeAmount = withdrawAmount * (withdrawFeePercent / 100);
  const netAmount = withdrawAmount - feeAmount;
  const cryptoPrice = cryptoPrices[selectedCrypto]?.price || 0;
  const cryptoEquivalent = cryptoPrice > 0 ? netAmount / cryptoPrice : 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <span className="badge badge-success"><CheckCircle className="w-3 h-3 mr-1" />Completed</span>;
      case 'rejected':
        return <span className="badge badge-danger"><XCircle className="w-3 h-3 mr-1" />Rejected</span>;
      default:
        return <span className="badge badge-warning"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="min-h-screen">
      <div className="animated-bg" />
      <div className="noise-overlay" />
      <Navbar />

      <main className="pt-32 pb-20 px-6" data-testid="withdraw-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <h1 className="text-4xl font-bold font-['Outfit'] text-white mb-4">Withdraw Funds</h1>
            <p className="text-slate-400">Withdraw your funds to your cryptocurrency wallet</p>
          </motion.div>

          {/* Live Crypto Prices Banner */}
          {Object.keys(cryptoPrices).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="grid grid-cols-3 gap-3 mb-8" data-testid="crypto-prices"
            >
              {cryptoOptions.map((c) => {
                const p = cryptoPrices[c.id];
                if (!p) return null;
                const isUp = p.change_24h >= 0;
                return (
                  <div key={c.id} className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                        <span className="text-white font-bold text-sm">{c.icon}</span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">{c.id}</p>
                        <p className="text-slate-500 text-xs">{c.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-semibold">${p.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                      <div className={`flex items-center gap-0.5 text-xs ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {Math.abs(p.change_24h).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Withdrawal Form */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5 text-orange-400" />
                Request Withdrawal
              </h2>

              {/* Balance Display */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 rounded-xl p-6 mb-6 border border-cyan-500/20">
                <p className="text-slate-400 text-sm mb-1">Available Balance</p>
                <p className="text-3xl font-bold text-white" data-testid="available-balance">
                  {formatCurrency(user?.balance)}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Crypto Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">Select Cryptocurrency</label>
                  <div className="grid grid-cols-3 gap-3">
                    {cryptoOptions.map((crypto) => (
                      <button key={crypto.id} type="button" onClick={() => setSelectedCrypto(crypto.id)}
                        className={`p-4 rounded-xl border transition-all ${selectedCrypto === crypto.id ? 'border-cyan-500 bg-cyan-500/10' : 'border-white/10 hover:border-white/20'}`}
                        data-testid={`crypto-${crypto.id.toLowerCase()}`}
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${crypto.color} flex items-center justify-center mx-auto mb-2`}>
                          <span className="text-white font-bold text-lg">{crypto.icon}</span>
                        </div>
                        <p className="text-white text-sm font-medium">{crypto.id}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Withdrawal Amount (USD)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">$</span>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                      className="input-field" style={{ paddingLeft: '2.5rem' }} placeholder="0.00" min={settings.min_withdrawal || 20}
                      max={user?.balance || 0} step="0.01" required data-testid="withdraw-amount"
                    />
                  </div>
                  {/* Quick amount buttons */}
                  <div className="flex gap-2 mt-2">
                    {[25, 50, 75, 100].map((pct) => (
                      <button key={pct} type="button"
                        onClick={() => setAmount(((user?.balance || 0) * pct / 100).toFixed(2))}
                        className="flex-1 py-1.5 rounded-lg text-xs font-medium text-slate-400 bg-white/5 border border-white/[0.06] hover:text-cyan-400 hover:border-cyan-500/30 transition-all"
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fee Calculator */}
                {withdrawAmount > 0 && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4 space-y-2" data-testid="fee-calculator"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-semibold text-white">Fee Breakdown</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Withdrawal Amount</span>
                      <span className="text-white">${withdrawAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Fee ({withdrawFeePercent}%)</span>
                      <span className="text-orange-400">-${feeAmount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-white/[0.06] pt-2 flex justify-between text-sm">
                      <span className="text-slate-300 font-medium">You Receive</span>
                      <span className="text-emerald-400 font-bold">${netAmount.toFixed(2)}</span>
                    </div>
                    {cryptoPrice > 0 && (
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-slate-500 text-xs">Approx. {selectedCrypto}</span>
                        <span className="text-slate-400 text-xs">{cryptoEquivalent.toFixed(selectedCrypto === 'BTC' ? 6 : 4)} {selectedCrypto}</span>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Wallet Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Your {selectedCrypto} Wallet Address</label>
                  <input type="text" value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)}
                    className="input-field font-mono text-sm" placeholder={`Enter your ${selectedCrypto} wallet address`}
                    required data-testid="wallet-address"
                  />
                </div>

                {/* Warning */}
                <div className="flex items-start gap-3 p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
                  <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-orange-300">
                    Please double-check your wallet address. Withdrawals to incorrect addresses cannot be recovered.
                  </p>
                </div>

                <button type="submit" disabled={loading || !user?.balance} className="btn-primary w-full flex items-center justify-center gap-2 !py-4 !text-base" data-testid="withdraw-submit">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><ArrowUpRight className="w-4 h-4" />Request Withdrawal</>
                  )}
                </button>
              </form>

              <div className="mt-6 space-y-3">
                <div className="p-4 bg-cyan-500/5 rounded-xl border border-cyan-500/15">
                  <p className="text-cyan-400 text-sm">
                    <strong>Processing Time:</strong> Withdrawal requests are typically processed within 24-48 hours.
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <Clock className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <p className="text-xs text-emerald-400/80">Funds will be credited instantly after processing</p>
                </div>
              </div>
            </motion.div>

            {/* Withdrawal History */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Withdrawal History
              </h2>

              <div className="space-y-4">
                {withdrawals.length > 0 ? (
                  withdrawals.map((withdrawal) => (
                    <div key={withdrawal.id} className="p-4 bg-white/5 rounded-xl border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-semibold">${withdrawal.amount.toLocaleString()}</span>
                        {getStatusBadge(withdrawal.status)}
                      </div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-slate-400">{withdrawal.crypto_type}</span>
                        <span className="text-slate-400">{formatDate(withdrawal.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono truncate">{withdrawal.wallet_address}</p>
                      {withdrawal.admin_notes && (
                        <p className="text-xs text-orange-400 mt-2">Note: {withdrawal.admin_notes}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <ArrowUpRight className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No withdrawals yet</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WithdrawPage;
